import {useMutation, useQuery} from '@apollo/react-hooks';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {Col, Image, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import {dateFormatFR} from '../../../utils';
import ColoredHeader from '../../../common/components/layout/ColoredHeader';
import MandatoryInput from '../../../common/components/layout/MandatoryInput';
import SelectType from '../../../common/components/Merchant/SelectType';
import {GRAPHQL_MUTATION_UPDATE_MERCHANT} from '../../../common/queries/merchants';
import ProfileFooter from './ProfileFooter';
import config from '../../../config/app';
import defaultBrand from '../../../common/assets/img/store.png';

const GRAPHQL_QUERY_MERCHANT = gql`
    {
        currentUser {
            id
            firstName
            lastName
            civility
            birthDate
            email
            mobilePhone
            postalCode
            city
            street
            createdAt
            merchant {
                id
                businessName
                contactEmail
                intraVatNumber
                phoneNumber
                salesArea
                siret
                type
                brand {
                    id
                    name
                    file {
                        id
                        token
                    }
                }
            }
        }
    }
`;

const initialInfos = {
  user: {
    firstName: '',
    lastName: '',
    civility: '',
    email: '',
    mobilePhone: '',
    birthDate: '',
    street: '',
    city: '',
    postalCode: '',
  },
  merchant: {
    businessName: '',
    contactEmail: '',
    phoneNumber: '',
    siret: '',
    salesArea: '',
    type: '',
    intraVatNumber: '',
  },
  brand: {
    name: '',
  },
};

const imgStyle = {
  maxHeight: 40,
  maxWidth: 80,
  height: 'auto',
  width: 'auto',
};

const infosStyle = {
  alignSelf: 'center',
  marginLeft: '15px',
  marginRight: '15px',
};

const headerInfos = {
  borderRadius: '5px',
  border: '1px solid #c8ced3',
  display: 'flex',
  justifyContent: 'space-between',
  height: '100%',
  marginLeft: '-5px',
  marginRight: '10px',
};

const CustomCol = props => <Col xs={4} xl={6}  {...props}/>;

const displayImage = (brand) => {
  const fileUrl = brand.file ? config.api_file_brand + brand.id + '?token=' + brand.file.token : defaultBrand;
  return <Image src={fileUrl} alt={brand.name} style={imgStyle}/>;
};

const ProfileMerchant = ({t, showSuccess, showError}) => {
  const [infos, setInfos] = useState(initialInfos);
  const {data} = useQuery(GRAPHQL_QUERY_MERCHANT);
  const [mutation, {loading}] = useMutation(GRAPHQL_MUTATION_UPDATE_MERCHANT);

  useEffect(() => {
    if (data && data.currentUser) {
      const {merchant, ...user} = data.currentUser;
      setInfos(infos => ({
        ...infos,
        merchant,
        user,
        brand: merchant.brand,
      }));
    }
  }, [data]);

  const setState = key => {
    return function(name, value) {
      setInfos({
        ...infos,
        [key]: {
          ...infos[key],
          [name]: value,
        },
      });
    };
  };

  const onSubmit = () => {
    const merchantInput = {
      businessName: infos.merchant.businessName,
      contactEmail: infos.merchant.contactEmail,
      phoneNumber: infos.merchant.phoneNumber,
      siret: infos.merchant.siret,
      salesArea: +infos.merchant.salesArea,
      type: infos.merchant.type,
      intraVatNumber: infos.merchant.intraVatNumber,
    };
    const userInput = {
      id: infos.user.id,
      civility: infos.user.civility,
      firstName: infos.user.firstName,
      lastName: infos.user.lastName,
      email: infos.user.email,
      postalCode: infos.user.postalCode,
      street: infos.user.street,
      city: infos.user.city,
      mobilePhone: infos.user.mobilePhone,
      birthDate: infos.user.birthDate,
    };

    const variables = {
      userInput,
      merchantInput,
    };

    mutation({variables})
      .then(() => {
        showSuccess();
      })
      .catch(() => {
        showError();
      });
  };

  return (
    <>
      <div style={headerInfos}>
        <div style={infosStyle}>
          {t('merchant.clientNumber')} : {infos.user.id}
        </div>
        <div style={infosStyle}>
          {infos.brand.name}
        </div>
        <div style={infosStyle}>
          {displayImage(infos.brand)}
        </div >
      </div>

      <br/>

      <Row className="section-info row">
        <ColoredHeader>{t('merchant.businessInfo')}</ColoredHeader>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('merchant.businessName')} : `}
            name="businessName"
            value={infos.merchant.businessName}
            onChange={setState('merchant')}
            show={infos.merchant.businessName === ''}/>
        </Col>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('merchant.siret')} : `}
            name="siret"
            value={infos.merchant.siret}
            onChange={setState('merchant')}
            show={infos.merchant.siret === ''}/>
        </Col>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('address')} : `}
            name="street"
            value={infos.user.street}
            onChange={setState('user')}
            show={infos.user.street === ''}/>
        </Col>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('merchant.intraVatNumber')} : `}
            name="intraVatNumber"
            value={infos.merchant.intraVatNumber}
            onChange={setState('merchant')}
            show={infos.merchant.intraVatNumber === ''}/>
        </Col>
        <CustomCol lg={2} xl={2}>
          <MandatoryInput
            title={`${t('merchant.postalCode')} : `}
            name="postalCode"
            value={infos.user.postalCode}
            onChange={setState('user')}
            show={infos.user.postalCode === ''}/>
        </CustomCol>
        <CustomCol lg={4} xl={4}>
          <MandatoryInput
            title={`${t('merchant.city')} : `}
            name="city"
            value={infos.user.city}
            onChange={setState('user')}
            show={infos.user.city === ''}/>
        </CustomCol>
        <CustomCol xl={6}>
          <MandatoryInput
            title={`${t('merchant.businessType')} : `}
            name="type"
            Component={SelectType}
            onChange={setState('merchant')}
            value={infos.merchant.type}
            show={infos.merchant.type === ''}/>
        </CustomCol>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('merchant.contactEmail')} : `}
            name="contactEmail"
            value={infos.merchant.contactEmail}
            onChange={setState('merchant')}
            show={infos.merchant.contactEmail === ''}/>
        </Col>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('merchant.salesArea')} : `}
            name="salesArea"
            value={infos.merchant.salesArea}
            onChange={setState('merchant')}
            show={infos.merchant.salesArea === ''}/>
        </Col>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('merchant.phoneNumber')} : `}
            name="phoneNumber"
            onChange={setState('merchant')}
            value={infos.merchant.phoneNumber}/>
        </Col>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('merchant.createdAt')} : `}
            name="createdAt"
            value={dateFormatFR({date: infos.user.createdAt})}
            disabled/>
        </Col>
      </Row>

      <br/>

      <Row className="section-info row">
        <ColoredHeader>{t('merchant.informations')}</ColoredHeader>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('dataTable.lastname')} : `}
            name="lastName"
            value={infos.user.lastName}
            onChange={setState('user')}
            show={infos.user.lastName === ''}/>
        </Col>
        <Col xs={6}>
          <MandatoryInput
            disabled
            title={`${t('dataTable.email')} : `}
            name="email"
            value={infos.user.email}/>
        </Col>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('dataTable.firstname')} : `}
            name="firstName"
            value={infos.user.firstName}
            onChange={setState('user')}
            show={infos.user.firstName === ''}/>
        </Col>
        <Col xs={6}>
          <MandatoryInput
            title={`${t('merchant.phoneNumber')} : `}
            name="mobilePhone"
            value={infos.user.mobilePhone}
            onChange={setState('user')}
            show={infos.user.mobilePhone === ''}/>
        </Col>
      </Row>
      <ProfileFooter onClick={onSubmit} loading={loading}/>
    </>
  );
};

ProfileMerchant.propTypes = {
  t: PropTypes.func.isRequired,
  showSuccess: PropTypes.func.isRequired,
  showError: PropTypes.func.isRequired,
};

export default withTranslation('user')(ProfileMerchant);
